import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { ModalContent } from '@modal/confirm-modal/confirm-modal.type';
import { ModalService } from '@modal/modal.service';
import { WooTableColumn } from '@presentational/woo-table/woo-table-column/woo-table-column.component';
import { WooTableComponent } from '@presentational/woo-table/woo-table.component';
import { WooComponentsModule } from '@scripts/woo_components.module/module';
import { EMPTY } from 'rxjs';
import { orderByDate } from '../../../../utils/date';
import { GrossRatingService } from '../../../../woo_services.module/GrossRatingService';
import { HistoryItem } from '../../../../woo_services.module/shared-types';
import { ProductsPageUtilityService } from '../../shared/products-page-utility-service';

@Component({
  selector: 'manage-gross-rating-factor',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    WooComponentsModule,
    WooTableComponent,
    WooTableColumn,
  ],
  templateUrl: './manage-gross-rating-factor.component.html',
  styleUrls: ['../../shared/table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ManageGrossRatingFactor implements OnInit {
  @Input('headerPos') parentHeader: HTMLElement;
  @Output() onClose = new EventEmitter<boolean>();

  readonly minimumGrossRatingFactor = 1;
  readonly alarminglyHighGrossRatingFactor = 6;
  readonly numberOfDecimals = 3;

  show = {
    loading: true,
    history: false,
    hasHistory: false,
  };

  historyGrossRatingData: HistoryItemView[] = [];
  grossRatingFactor: number;
  tableHeaders = ['Omvandlingsnyckel (tidigare värde -> nytt värde)', 'Uppdaterad', 'Användare'];

  constructor(
    private grossFactorService: GrossRatingService,
    private utilityService: ProductsPageUtilityService,
    private modalService: ModalService,
    private changeDetection: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.load().finally(() => {
      this.show.loading = false;
      this.changeDetection.markForCheck();
    });
  }

  load = async (): Promise<void> => {
    const factor = await this.grossFactorService.getGrossRatingFactor();
    this.grossRatingFactor = factor.gross_rating_factor;
    const historyData = await this.grossFactorService.getGrossRatingHistoryData();
    if (historyData.length > 0) {
      this.historyGrossRatingData = orderByDate(historyData, 'updated_at')
        .map(
          (historyItem: HistoryItem): HistoryItemView => {
            return {
              updater: historyItem['updater_name'],
              updatedAt: historyItem.updated_at,
              grossRatingFactor: this.formatHistoryGrossRatingFactor(historyItem),
            };
          },
        )
        .filter((historyItemView: HistoryItemView): boolean => !!historyItemView.grossRatingFactor);
      this.show.hasHistory = this.historyGrossRatingData.length > 0;
    }
    this.changeDetection.markForCheck();
  };

  formatHistoryGrossRatingFactor(historyItem: HistoryItem): string | null {
    if ('gross_rating_factor' in historyItem['modified'] && 'gross_rating_factor' in historyItem['original']) {
      return `${historyItem['original']['gross_rating_factor']['$numberDecimal']} -> ${historyItem['modified']['gross_rating_factor']['$numberDecimal']}`;
    }
    return null;
  }

  disableSaveButton(): boolean {
    return this.grossRatingFactor < this.minimumGrossRatingFactor;
  }

  saveConfirmationDialog(): ModalContent {
    if (this.grossRatingFactor < this.alarminglyHighGrossRatingFactor)
      return {
        title: 'Bekräfta ändringar',
        texts: [`Vill du spara omvandlingsnyckel ${this.grossRatingFactor} ?`],
        confirmLabel: 'Spara',
        cancelLabel: 'Avbryt',
      };

    return {
      title: 'Bekräfta ändringar',
      texts: [''],
      confirmLabel: 'Spara',
      cancelLabel: 'Avbryt',
      warnings: [
        {
          title: 'Varning!',
          texts: [`Du är på väg att sätta omvandlingsnyckeln till ${this.grossRatingFactor}.`, 'Är du säker?'],
        },
      ],
    };
  }

  formatGrossRatingFactorDecimals(): number {
    return Number(this.grossRatingFactor.toFixed(this.numberOfDecimals));
  }

  save = async (): Promise<void> => {
    this.modalService.openConfirm(this.saveConfirmationDialog()).then((response) => {
      if (response.confirm) {
        this.show.loading = true;
        this.grossFactorService
          .updateGrossRatingFactor(this.formatGrossRatingFactorDecimals())
          .then(async () => {
            await this.load();
            this.show.loading = false;
            this.changeDetection.markForCheck();
          })
          .catch((err) => {
            EMPTY;
            this.modalService.openError('Ajdå, något gick snett. Kunde inte spara ändringar.', err.error.error);
            return EMPTY;
          })
          .finally(() => {
            this.show.loading = false;
            this.changeDetection.markForCheck();
          });
      }
    });
  };

  openCard(): void {
    this.show.history = true;
  }

  closeCard(): void {
    this.show.history = false;
  }
}

interface HistoryItemView {
  updater: string;
  updatedAt: string | Date;
  grossRatingFactor?: string;
}
